import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "React context without context, using XState – CodeWithSwiz 14, 15",
  "description": "useAuth works without React Context! 🎉 here's how",
  "published": "2020-11-10T00:00:00.000Z",
  "categories": "CodeWithSwiz, Technical, XState, Livecoding",
  "hero": "./img/screenshot-1605026578121.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "https://github.com/Swizec/useAuth"
      }}>{`useAuth`}</a>{` works without React Context! 🎉`}</p>
    <p><em parentName="p">{`CodeWithSwiz is a twice-a-week live show. Like a podcast with video and fun hacking. Focused on experiments. `}<a parentName="em" {...{
          "href": "https://youtube.com/swizecteller"
        }}>{`Join live Wednesdays and Sundays`}</a></em></p>
    <p>{`This was quite the saga my friend. Started on October 8th, thought, `}<em parentName="p">{`"Pfft, how hard can it be"`}</em>{`, and here we are a month later. 😅`}</p>
    <p>{`I made a `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/useAuth/pull/107"
      }}>{`pull request with the full diff`}</a>{`, if you're curious. Read about steps 1 through 3 👉 `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/refactoring-a-usereducer-to-xstate-pt1-codewithswiz-11"
      }}>{`Refactoring a useReducer to XState`}</a>{`, `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/how-to-write-tests-for-xstate-codewithswiz-12"
      }}>{`Writing tests for XState`}</a>{`, `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/swap-usereducer-with-xstate-codewithswiz-13"
      }}>{`Swap useReducer with XState`}</a>{`.`}</p>
    <p>{`Today's post is about the last part: Tossing react context.`}</p>
    <lite-youtube {...{
      "videoid": "IXhJK8FjALg",
      "videostartat": "0"
    }}></lite-youtube>
    <lite-youtube {...{
      "videoid": "XzE32ZAW87I",
      "videostartat": "0"
    }}></lite-youtube>
    <h2 {...{
      "id": "why-react-context"
    }}>{`Why React Context`}</h2>
    <p>{`React Context is the workhorse behind `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/wormhole-state-management"
      }}>{`wormhole state management`}</a>{` – the idea that hooks should feel like magic. Call `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{` anywhere in any component and it knows everything about the user.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function RandomComponent() {
  const { user } = useAuth()

  return <h2>Hello {user.username}</h2>
}
`}</code></pre>
    <p>{`That's what an API in modern React should look like. No prop drilling, no fumbling with global state, no worries about performance. Call the hook, get the info.`}</p>
    <p><inlineCode parentName="p">{`useAuth`}</inlineCode>{` achieves that with context.`}</p>
    <p>{`A global provider wraps your React tree. It holds a piece of global state and shares it with everyone via context. `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{` hooks into this state with a `}<inlineCode parentName="p">{`useContext`}</inlineCode>{` call.`}</p>
    <p>{`Like this example from the `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/wormhole-state-management"
      }}>{`Wormhole state management`}</a>{` article:`}</p>
    <iframe {...{
      "src": "https://codesandbox.io/embed/wormhole-state-pattern-5-j4w5e",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <p>{`A couple counters sharing state via context. You get a neat API.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const { count, incB } = useSharedCount()
`}</code></pre>
    <p>{`But the machinery to make this work is gnarly.`}</p>
    <p>{`Avoiding unnecessary re-renders involves 2 sets of state. One for context, one for machinery around context. You have to be careful how context updates.`}</p>
    <p>{`For `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{`, it looked like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`export const AuthProvider: AuthProviderInterface = ({ ... }) => {
    // omitted config code

    // Holds authentication state
    const [state, dispatch] = useReducer<React.Reducer<AuthState, AuthAction>>(
        authReducer,
        getDefaultState()
    );

    const [contextValue, setContextValue] = useState<AuthContextState>({
        state,
        dispatch,
        auth0,
        callback_domain: callbackDomain,
        customPropertyNamespace,
        navigate
    });

    // Update context value and trigger re-render
    // This patterns avoids unnecessary deep renders
    // https://reactjs.org/docs/context.html#caveats
    useEffect(() => {
        setContextValue((contextValue: AuthContextState) => ({
            ...contextValue,
            state
        }));
    }, [state]);

    // omitted auth check code

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};
`}</code></pre>
    <p>{`Not intimidating at all 😅`}</p>
    <p>{`Functions inside `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{` change state via methods they grab out of context, effect in provider sees the change, updates context, triggers re-renders.`}</p>
    <p>{`Works great and means users have to wrap their code in `}<inlineCode parentName="p">{`<AuthProvider>`}</inlineCode>{`.`}</p>
    <p>{`You know what would make useAuth feel more magical? No global provider 😍`}</p>
    <h2 {...{
      "id": "using-xstate-over-context"
    }}>{`Using XState over Context`}</h2>
    <p>{`XState introduces the concept of `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Actor_model"
      }}>{`actors`}</a>{` – independent objects that hold their own state and respond to events.`}</p>
    <p>{`Every `}<em parentName="p">{`interpreted`}</em>{` XState state machine is an actor. The state machine defines how the actor responds to events, the object is the actor.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { interpret } from "xstate"

export const authService = interpret(authMachine)
authService.start()
`}</code></pre>
    <p>{`Because you export an `}<em parentName="p">{`instance`}</em>{` of an object, you can access that instance anywhere. Your bundler ensures it's the same instance everywhere.`}</p>
    <p><inlineCode parentName="p">{`import { authService } from 'authService.ts'`}</inlineCode>{` doesn't call `}<inlineCode parentName="p">{`interpret(authMachine)`}</inlineCode>{`. It returns the memoized result. How that works depends on the bundler you're using.`}</p>
    <p>{`What that means for you is that you now have global shared state without a context provider.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media4.giphy.com/media/QWw4hc5gTnJhY0BUI3/giphy-loop.mp4?cid=7cf28b0fm0spqye9yivo0m9wev6so3lq3psmepf2ehi5r2ko&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`Thanks to `}<a parentName="p" {...{
        "href": "https://xstate.js.org/docs/packages/xstate-react/"
      }}>{`XState's React integration`}</a>{`, you can safely access this shared state.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`export const useAuth: useAuthInterface = () => {
  const [state, eventSend] = useService(authService)

  // ...
}
`}</code></pre>
    <p><inlineCode parentName="p">{`state`}</inlineCode>{` is the state machine state – `}<inlineCode parentName="p">{`unauthenticated`}</inlineCode>{`, `}<inlineCode parentName="p">{`authenticating`}</inlineCode>{`, `}<inlineCode parentName="p">{`authenticated`}</inlineCode>{`, `}<inlineCode parentName="p">{`error`}</inlineCode>{`. The application state lives in `}<inlineCode parentName="p">{`state.context`}</inlineCode>{`. You can read more about that in `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/refactoring-a-usereducer-to-xstate-pt1-codewithswiz-11"
      }}>{`Refactoring a useReducer to XState`}</a>{`.`}</p>
    <p><inlineCode parentName="p">{`eventSend`}</inlineCode>{` lets you dispatch events to transition between states. `}<inlineCode parentName="p">{`LOGIN`}</inlineCode>{` to start authenticating, `}<inlineCode parentName="p">{`LOGOUT`}</inlineCode>{` to logout, etc. Available transitions depend on the current state of the state machine.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/8387c14628b38c89db2941a324836a6a/d4b10/useAuth-state-machine07g9i3.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "47.98206278026906%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'192\\'%20viewBox=\\'0%200%20400%20192\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M22%2014l-1%201-1-1-1-1-2%201h-1c-1-1-5-1-6%201-1%201-1%201-1-1-1-1-1-1-1%201%200%203%200%203%206%203l6-1h1c1%201%209%201%209-1h1l2%202%202-2h1l1%202v-5h-6l-1%201-1%201h-1l-1-2-1%202-1-1c-1-2-3-3-3%200M12%2038c0%202%206%209%207%208l1%203c0%206%200%206%2032%206a135%20135%200%200135%202c3%200%204%200%205%202l2%203h22c24%200%2025%200%2025-6%200-5-3-5-26-5-21%200-21%201-23%203l-4%202c-2%200-2%200-2-6%200-7%202-8%207-3%203%203%2021%203%2024%200s3-3%2021-3c15%200%2018%200%2019%202%201%201%201%201%202-1%201-1%200-2-1-3h-3l-18%201c-17%200-17%200-20-2-2-3-3-3-12-3s-10%200-12%203c-3%203-7%203-7%200v-2l-31-1c-33%200-35%200-35%203l-2%202c-3%200-4-2-2-3%201-2%200-4-2-4l-2%202m245%203c-2%202-6%203-6%200a194%20194%200%20001%2030h1c1-1%202%200%204%202s2%202%2018%202c17%200%2017%200%2017%202s0%202-2%201c-2%200-2%200-1%201v3c-1%201-1%201%201%201%201%200%202%200%202%204v4h-62l-63%201c-2%200-2-1-2-3v-4h42l43-1h-85v-8h5c4%200%205%200%205-2s0-2-4-2c-5%201-6%200-6-6l-1%207v19h-17l-16%201h16c15%200%2017%200%2017%202h1c0-3%206-3%2068-3%2058%200%2059%200%2059%202l-1%202v2c2%203%203%201%202-3v-3h12l13-1h-25v-6c0-4%200-5%201-3l1%202v-2c0-2%200-2%201-1h2l1%202%202%201c2%200%202-1%202-3s-1-2-3-2l-3-1h-1c-2%201-3%200-3-2l5-1%206-2c4-4%201-8-6-8l-5-1c0-2%202-2%208-2%209%200%2012-1%2012-4l2-1c2%200%202%200%201-1l-4-2c-1-2-3-2-10-2-9%200-9-1-10-3l-1-2v5h-16c-16%200-17%200-18%202l-4%202c-2%200-2%200-2-6%200-7%202-8%206-3%204%203%2024%203%2028%200l4-3v-1l-4-2c-3-3-4-3-14-3s-11%200-14%203M21%2046l1%207h63V40H21v6m22-2h-2l-1%202h-1l-1-1h-1c-2-1-3-1-3%201h-1c-2-3-4-2-4%201%200%202%200%202%201%201l1-1c0%202%205%202%206%201h1c0%202%208%201%208-1h1c0%202%205%202%206%201h2l1-1c0%201%202%202%2013%202h13v-3c0-2%200-3-1-2H68l-5%201c-1%201-1%201-1-1h-6l-2%201h-1l-3-1h-3c-2-1-2-1-2%202v3l-1-3c0-3-1-3-1-2m131%200l-1%201c-1-1-1%200-1%201h-1c-1-2-5-2-6%201%200%202%200%202%206%202s7-1%207-2h1c0%202%208%203%208%200h1c0%201%203%202%2010%202h9v-3c0-2%200-3-1-2h-6l-5%201c-1%201-1%201-1-1h-20m26%2013l-1%201c0-3-3-2-4%200s-1%202-1%200c-1-2-1-2-3-1l-1%201c0-3-4-2-5%201%200%202%200%202%205%202s6%200%206-2l1%201h5c2%201%206%201%206-1h1l1%202%202-2h1c0%202%207%202%208%200h1c0%201%202%202%209%202l9-1%201-2%201%201%201%203c2%202%204%200%204-3%200-2-1-2-10-2l-10-1h-14c-2-1-6%201-6%203h-1l-1-2c-1%201-2%200-2-1%200-2-2-1-3%201m51%206c0%205%200%206%202%206l4-2c1-2%202-2%2018-2l17-1c0-2-2-2-16-2-17%200-17%200-19-2-4-5-6-4-6%203m-132%201l-10%201c-4%201-3%204%201%205l4-1h2c0-2%200-2%201-1l5%202a4213%204213%200%20006-3h1l1%202%202-2h1l1%202%201%201c-1%201%200%201%201%201%202%200%202-1%202-3%200-3%200-3-2-3h-2c-1-1-9%201-9%202l-1%201v-3l-2%202c-2%201-4%201-4-1l2-1h2l-3-1m77%208h-1l-8-1c-6%200-6%201-6%203l5%201%205%201h4l2-1h1c0%202%2010%201%2010-1h1l4%202%203-2h1c0%203%2019%203%2019%200l1%201%201%201h1c1%202%204%201%204-2s-2-3-4-2h-1l-2-1h-2l-1%202-1%203v-3c0-2%200-3-1-2h-9l-1-1-2%201h-4l-4%201h-2c-2-2-7-1-7%202l-1-1c-1-2-1-2-1%200l-1-1c-1-2-3-3-3%200m-70%2032c-3%203-7%203-7%200l-1-2v67l1-24c0-22%200-24%202-24l2-1-2-1c-2%200-2-1-2-6%200-7%202-8%207-3%202%202%202%202%2015%202s14%200%2016-2c1-1%202-3%204-3v-1l-4-2c-3-3-3-3-15-3-13%200-13%200-16%203m-93%203l-1%201c-1-1-1%200-1%201%200%202%200%202-1%201-1-3-5-3-6%200%200%202%200%202%206%202s7%200%207-2h1c0%202%208%203%208%200h1c0%202%202%202%2013%202h13v-3c0-3-1-4-3-1H54c-1%201-1%200-1-1H33m93%2010c-2%203-2%204%200%206%203%203%2044%203%2046%200%202-2%202-4%200-6-1-2-4-2-23-2-21%200-22%200-23%202m-66%202c0%202%200%202-1%201h-8c-2-1-2-1-3%201h-1c-1-2-3-2-3%201%200%202%201%202%205%202l5-1h6l8%201%207-1%201-2c0-2-6-2-7%200h-1l-1-2h-2l-1-1-1%203c0%203-1%202-2-1l-1-1m17%202v3h9c8%200%209%200%209-2h1c1%202%207%203%207%201h1c1%202%202%201%203-1h1l1%202v-5l-3%201h-4c0-1-1-1-2%201h-1c0-2-8-2-10%200-3%202-4%202-4%200l3-1v-1h-5l-3%201h-1c1-2%200-2-1-2s-2%201-1%203m-17%209l1%203%201%201h30c4%201%207%200%207-2l1%201c1%201%201%202%202%201l2%201h6c1%200%202-1%202-3s0-2-9-2l-14-1h-3c0%202-1%202-1%201h-7c-5%200-6%201-6%202%200%202-3%201-3-1%200-1%200-2-1-1l-4-1-2-1c-2%200-2%200-2%202m190-1c-1%202-11%202-11%201-1-1-1%200-1%201%200%203-2%203-2%200s-3-2-4%201h-1c0-2-3-3-3-1h-1l-2-1h-2l-5%201c-2%202%201%204%206%203%209%200%209%200%209-2l2%201c2%202%202%202%203%201%201-2%202-2%202-1%201%202%205%202%207%201h1l1-1%201-1c0%202%201%202%203%201v-3l2-1h-5m18%200l-7%201c-6%200-6%200-6%202s0%202%201%201l1-1c0%203%206%203%206%200l1%201%201%202v-2l1-1c0%202%201%202%207%202%205%200%206%200%206-2h1c1%202%202%202%209%202%205%200%205%200%205-2-1-3-3-3-5%200h-1c1-1%200-2-1-2h-5c0%201-8%201-9-1l-3-1-2%201m-46%2011h-1c-1%200-4%202-3%203%200%202%2015%202%2015%200h1c2%203%2031%202%2031%200h1l3%202%203%201h1c1-1%202-2%203-1l2-2c1-2-1-4-2-2h-2l-7-1h-8l-4-1c-3%200-4%201-4%202%200%202%200%202-1%201-1-2-11-2-12%200%200%202-2%201-2-1%200-3-3-2-4%201h-1c0-2-3-3-3%200h-1l-3-2h-2m-131%205h-7c-7%200-7%200-7%203%200%202%200%202%201%201l1-1c1%202%206%202%206%200l1%201%201%202v-4l1%202h2c3%201%2011%201%2011-1l1%201%201%202v-2l1-1c0%202%205%203%205%201h1c1%202%202%201%203-1h1l1%202v-3l-1-2-4%201c-2-1-3%200-4%201-1%202-1%202-1%200s0-3-1-2h-4l-6%202-2%201c-1-2%200-3%202-2l1-1h-4m-35%2010l-3%201-3%201h-1l-2-1h-5c-5%202-3%203%205%204%207%200%208%200%208-2h1c0%202%201%202%209%202h11c4%201%2011%200%2011-2l1%201c1%201%201%202%202%201l3%201h3l1-1c1%201%203%200%203-2%200-1-1-2-4-2-2%200-3%200-2%201l1%202-2-1c-1-2-3-2-7-2h-5c-1-3-10-1-12%202l-1-1-4-1c-3%200-4%201-4%202l-1%201-1-2c0-2-2-4-2-2m46%2030\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/8387c14628b38c89db2941a324836a6a/ca0a1/useAuth-state-machine07g9i3.webp 223w", "/static/8387c14628b38c89db2941a324836a6a/75680/useAuth-state-machine07g9i3.webp 445w", "/static/8387c14628b38c89db2941a324836a6a/8d1ba/useAuth-state-machine07g9i3.webp 890w", "/static/8387c14628b38c89db2941a324836a6a/3838e/useAuth-state-machine07g9i3.webp 1335w", "/static/8387c14628b38c89db2941a324836a6a/7f80f/useAuth-state-machine07g9i3.webp 1394w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/8387c14628b38c89db2941a324836a6a/e92b6/useAuth-state-machine07g9i3.png 223w", "/static/8387c14628b38c89db2941a324836a6a/e66bf/useAuth-state-machine07g9i3.png 445w", "/static/8387c14628b38c89db2941a324836a6a/4ef49/useAuth-state-machine07g9i3.png 890w", "/static/8387c14628b38c89db2941a324836a6a/4e814/useAuth-state-machine07g9i3.png 1335w", "/static/8387c14628b38c89db2941a324836a6a/d4b10/useAuth-state-machine07g9i3.png 1394w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/8387c14628b38c89db2941a324836a6a/4ef49/useAuth-state-machine07g9i3.png",
                "alt": "useAuth state machine",
                "title": "useAuth state machine",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`useAuth state machine`}</figcaption>{`
  `}</figure></p>
    <p>{`When state updates, entry and exit actions update `}<inlineCode parentName="p">{`state.context`}</inlineCode>{`. When your components depend on those values, they re-render.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media0.giphy.com/media/3o84U6421OOWegpQhq/giphy-loop.mp4?cid=7cf28b0fr1rs03w4dmeounwsl485qgb8byj23yn26enwqeuq&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <h2 {...{
      "id": "what-about-configuration"
    }}>{`What about configuration?`}</h2>
    <p>{`A common pattern is to use the global context provider as an API for configuration. I used the same approach in `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`export const wrapRootElement = ({ element }) => (
  <AuthProvider
    navigate={navigate}
    auth0_domain="useauth.auth0.com"
    auth0_client_id="GjWNFNOHq1ino7lQNJBwEywa1aYtbIzh"
  >
    {element}
  </AuthProvider>
)
`}</code></pre>
    <p>{`Without a provider, how do users configure your library?`}</p>
    <p>{`With a new event!`}</p>
    <p>{`I kept the `}<inlineCode parentName="p">{`<AuthProvider>`}</inlineCode>{` as a convenience. Accepts configuration params and passes them into the XState machinery. You can do it without the provider.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`export const AuthProvider: AuthProviderInterface = ({ ... }) => {
    // omitted config munching

    const { dispatch } = useAuth();

    useEffect(() => {
        const auth0 = new Auth0.WebAuth({ ...params, ...auth0_params });
        dispatch("SET_CONFIG", {
            authProvider: auth0,
            navigate,
            customPropertyNamespace,
            callbackDomain
        });
    }, [navigate, customPropertyNamespace, callbackDomain]);

    return <React.Fragment>{children}</React.Fragment>;
};
`}</code></pre>
    <p>{`Notice the provider is using the hook. Like any other component would. 🤯`}</p>
    <p>{`The `}<inlineCode parentName="p">{`SET_CONFIG`}</inlineCode>{` event configures `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{`. And in a move towards supporting many auth providers, the library now accepts a configured auth provider object.`}</p>
    <p>{`🤘`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: the React.Fragment bit is interesting, components can't return `}<inlineCode parentName="p">{`children`}</inlineCode>{` without wrapping in a React element. Who knew`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      